import React, { useEffect, useMemo, useState } from "react";
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Nav,
  NavItem,
  NavLink,
  TabContent,
  TabPane,
  Input,
  Label,
  FormGroup,
  Spinner,
  Alert,
} from "reactstrap";
import { GetData, PostData } from "../../services/api";

/**
 * Modal reutilizable para gestionar la membresía de un contacto en listas.
 * - Pestaña "Agregar a listas": multi-select de listas donde el contacto AÚN no está.
 * - Pestaña "Mover de lista": saca de una lista origen y lo mete a una destino.
 *
 * Publica (vía backend) el evento de Kafka correspondiente:
 *   agregar -> contact_added_to_list · mover -> contact_removed_from_list + contact_added_to_list
 *
 * Props:
 *   isOpen, toggle       control del modal
 *   customerId           id del contacto
 *   currentLists         listas actuales del contacto: [{id, name}]
 *   onDone(result)       callback tras una operación exitosa (para refrescar la vista padre)
 */
const ManageContactListsModal = ({ isOpen, toggle, customerId, currentLists = [], onDone }) => {
  const [activeTab, setActiveTab] = useState("add");
  const [allLists, setAllLists] = useState([]);
  const [loadingLists, setLoadingLists] = useState(false);
  const [selectedToAdd, setSelectedToAdd] = useState([]);
  const [moveFrom, setMoveFrom] = useState("");
  const [moveTo, setMoveTo] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const currentIds = useMemo(
    () => new Set((currentLists || []).map((l) => String(l.id))),
    [currentLists]
  );

  // Cargar todas las listas al abrir
  useEffect(() => {
    if (!isOpen) return;
    setError("");
    setSuccess("");
    setSelectedToAdd([]);
    setMoveFrom((currentLists[0] && String(currentLists[0].id)) || "");
    setMoveTo("");
    setActiveTab("add");
    (async () => {
      try {
        setLoadingLists(true);
        const res = await GetData("/contact-lists");
        const data = (res && res.data) || [];
        setAllLists(Array.isArray(data) ? data : data.items || []);
      } catch (e) {
        setError("No se pudieron cargar las listas");
      } finally {
        setLoadingLists(false);
      }
    })();
  }, [isOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  // Listas donde el contacto NO está (candidatas para "agregar")
  const addableLists = useMemo(
    () => allLists.filter((l) => !currentIds.has(String(l.id))),
    [allLists, currentIds]
  );

  // Listas destino para "mover": todas menos la origen seleccionada
  const moveTargets = useMemo(
    () => allLists.filter((l) => String(l.id) !== String(moveFrom)),
    [allLists, moveFrom]
  );

  const toggleSelect = (id) => {
    const key = String(id);
    setSelectedToAdd((prev) =>
      prev.includes(key) ? prev.filter((x) => x !== key) : [...prev, key]
    );
  };

  const handleAdd = async () => {
    if (!selectedToAdd.length) {
      setError("Selecciona al menos una lista");
      return;
    }
    setSubmitting(true);
    setError("");
    setSuccess("");
    try {
      const res = await PostData(`/contact-lists/customers/${customerId}/lists`, {
        listIds: selectedToAdd,
      });
      const added = (res && res.data && res.data.added) || [];
      setSuccess(`Agregado a ${added.length} lista(s)`);
      if (onDone) await onDone(res && res.data);
      setTimeout(() => toggle(), 700);
    } catch (e) {
      setError("No se pudo agregar a las listas");
    } finally {
      setSubmitting(false);
    }
  };

  const handleMove = async () => {
    if (!moveFrom || !moveTo) {
      setError("Selecciona lista origen y destino");
      return;
    }
    if (String(moveFrom) === String(moveTo)) {
      setError("La lista origen y destino no pueden ser la misma");
      return;
    }
    setSubmitting(true);
    setError("");
    setSuccess("");
    try {
      const res = await PostData(`/contact-lists/customers/${customerId}/move`, {
        fromListId: moveFrom,
        toListId: moveTo,
      });
      setSuccess("Contacto movido de lista");
      if (onDone) await onDone(res && res.data);
      setTimeout(() => toggle(), 700);
    } catch (e) {
      setError("No se pudo mover el contacto");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal isOpen={isOpen} toggle={toggle} centered>
      <ModalHeader toggle={toggle}>Gestionar listas del contacto</ModalHeader>
      <ModalBody>
        <Nav tabs className="mb-3">
          <NavItem>
            <NavLink
              style={{ cursor: "pointer" }}
              active={activeTab === "add"}
              onClick={() => setActiveTab("add")}
            >
              <i className="ri-add-line me-1"></i> Agregar a listas
            </NavLink>
          </NavItem>
          <NavItem>
            <NavLink
              style={{ cursor: "pointer" }}
              active={activeTab === "move"}
              onClick={() => setActiveTab("move")}
            >
              <i className="ri-arrow-left-right-line me-1"></i> Mover de lista
            </NavLink>
          </NavItem>
        </Nav>

        {error && <Alert color="danger" className="py-2">{error}</Alert>}
        {success && <Alert color="success" className="py-2">{success}</Alert>}

        {loadingLists ? (
          <div className="text-center py-3">
            <Spinner size="sm" /> Cargando listas…
          </div>
        ) : (
          <TabContent activeTab={activeTab}>
            {/* ── Agregar a varias listas ── */}
            <TabPane tabId="add">
              {addableLists.length === 0 ? (
                <p className="text-muted mb-0">El contacto ya está en todas las listas.</p>
              ) : (
                <div style={{ maxHeight: 260, overflowY: "auto" }}>
                  {addableLists.map((l) => (
                    <FormGroup check key={l.id} className="mb-2">
                      <Input
                        type="checkbox"
                        id={`add-list-${l.id}`}
                        checked={selectedToAdd.includes(String(l.id))}
                        onChange={() => toggleSelect(l.id)}
                      />
                      <Label check for={`add-list-${l.id}`} className="ms-1">
                        {l.name}
                      </Label>
                    </FormGroup>
                  ))}
                </div>
              )}
            </TabPane>

            {/* ── Mover de una lista a otra ── */}
            <TabPane tabId="move">
              <FormGroup>
                <Label>Lista origen</Label>
                <Input
                  type="select"
                  value={moveFrom}
                  onChange={(e) => setMoveFrom(e.target.value)}
                >
                  <option value="">— Selecciona —</option>
                  {(currentLists.length ? currentLists : allLists).map((l) => (
                    <option key={l.id} value={l.id}>
                      {l.name}
                    </option>
                  ))}
                </Input>
              </FormGroup>
              <FormGroup>
                <Label>Lista destino</Label>
                <Input
                  type="select"
                  value={moveTo}
                  onChange={(e) => setMoveTo(e.target.value)}
                >
                  <option value="">— Selecciona —</option>
                  {moveTargets.map((l) => (
                    <option key={l.id} value={l.id}>
                      {l.name}
                    </option>
                  ))}
                </Input>
              </FormGroup>
              <small className="text-muted">
                El contacto saldrá de la lista origen y entrará a la destino.
              </small>
            </TabPane>
          </TabContent>
        )}
      </ModalBody>
      <ModalFooter>
        <Button color="light" onClick={toggle} disabled={submitting}>
          Cancelar
        </Button>
        {activeTab === "add" ? (
          <Button color="primary" onClick={handleAdd} disabled={submitting || loadingLists}>
            {submitting ? <Spinner size="sm" /> : "Agregar a listas"}
          </Button>
        ) : (
          <Button color="primary" onClick={handleMove} disabled={submitting || loadingLists}>
            {submitting ? <Spinner size="sm" /> : "Mover"}
          </Button>
        )}
      </ModalFooter>
    </Modal>
  );
};

export default ManageContactListsModal;
